import * as Sentry from '@sentry/react';

// Sentry for the SecretsOverflow web frontend, a separate project from the API
// (org `secretsfl`, project `secretsoverflow_frontend`) with its own DSN.
//
// Everything is gated on VITE_SENTRY_DSN: with no DSN (local dev, PR builds, or
// before the project exists) init is a no-op and captureException/captureMessage
// simply do nothing.

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const ENVIRONMENT = (import.meta.env.VITE_ENV as string | undefined) || 'development';
const RELEASE = import.meta.env.VITE_APP_VERSION as string | undefined;
// Optional first-party tunnel (a backend route forwarding envelopes) to dodge
// ad blockers. Unset → events go straight to Sentry.
const TUNNEL = import.meta.env.VITE_SENTRY_TUNNEL as string | undefined;

const TRACES_SAMPLE_RATE = ENVIRONMENT === 'production' ? 0.2 : 1.0;

/** Sentry's own envelope endpoint, derived from the DSN. */
export function ingestUrlFromDsn(dsn: string): string | null {
  try {
    const url = new URL(dsn);
    const projectId = url.pathname.replace(/^\//, '').split('/')[0];
    if (!projectId || !url.username) return null;
    return `${url.protocol}//${url.host}/api/${projectId}/envelope/?sentry_key=${url.username}&sentry_version=7`;
  } catch {
    return null;
  }
}

// Derived from the SDK's own signatures rather than deep-importing its types,
// which aren't re-exported by name.
type TransportOptions = Parameters<typeof Sentry.makeFetchTransport>[0];
type Transport = ReturnType<typeof Sentry.makeFetchTransport>;
type TransportRequest = Parameters<Transport['send']>[0];

/**
 * The tunnel routes envelopes through our own API so ad blockers don't eat
 * them. That couples error reporting to the health of the thing most likely to
 * be broken: while the API was down, nothing reached Sentry at all, including
 * the errors about the API being down.
 *
 * Try the tunnel first, and if it fails or answers with an error, send the same
 * envelope straight to Sentry. The envelope carries its own DSN header (the SDK
 * adds it whenever `tunnel` is set), so both routes accept it.
 */
function makeTunnelWithFallback(
  tunnelUrl: string,
  ingestUrl: string,
): (options: TransportOptions) => Transport {
  return (options) => {
    const viaTunnel = Sentry.makeFetchTransport({ ...options, url: tunnelUrl });
    const direct = Sentry.makeFetchTransport({ ...options, url: ingestUrl });
    return {
      send: async (request: TransportRequest) => {
        try {
          const response = await viaTunnel.send(request);
          // The tunnel answers 502 when it can't reach Sentry itself.
          if (response.statusCode === undefined || response.statusCode < 400) return response;
        } catch {
          // Fall through and try Sentry directly.
        }
        return direct.send(request);
      },
      flush: async (timeout?: number) => {
        const [tunnelled, sent] = await Promise.all([
          viaTunnel.flush(timeout),
          direct.flush(timeout),
        ]);
        return tunnelled && sent;
      },
    };
  };
}

export function initSentry(): void {
  if (!DSN) return;

  // Only swap the transport when we have both ends of the fallback. Anything
  // unexpected in the DSN and we leave the SDK's own behaviour alone.
  const ingestUrl = TUNNEL ? ingestUrlFromDsn(DSN) : null;
  const transport =
    TUNNEL && ingestUrl ? makeTunnelWithFallback(TUNNEL, ingestUrl) : undefined;

  Sentry.init({
    dsn: DSN,
    environment: ENVIRONMENT,
    release: RELEASE,
    // Keep `tunnel` set even with the custom transport: it's what makes the SDK
    // put the DSN in the envelope header, which our tunnel route reads to know
    // where to forward.
    tunnel: TUNNEL || undefined,
    transport,
    integrations: [Sentry.browserTracingIntegration()],
    // Known-benign noise that is never a real app bug.
    //
    // Deliberately NOT filtered: "Failed to fetch" / "NetworkError when
    // attempting to fetch resource". Those were in here and they are exactly
    // what our own API returns when it is down, so an outage reported nothing
    // at all. Some ad-blocker and flaky-connection noise is the price of
    // hearing about a real outage.
    ignoreErrors: [
      /ResizeObserver loop (limit exceeded|completed)/,
      /AbortError/,
      'The operation was aborted.',
      'Non-Error promise rejection captured',
      // Stale-chunk / dynamic-import failures after a deploy: a client on the old
      // build requests a chunk that no longer exists and gets index.html (HTML)
      // back. The vite:preloadError handler reloads for critical chunks and the
      // optional lazy imports below swallow their own failures, so these are
      // transient and not actionable.
      /valid JavaScript MIME type/i,
      /Importing a module script failed/i,
      /dynamically imported module/i,
      // Facebook's in-app browser talking to its own native bridge. Their
      // injected script (iabjs://navigation_performance_logger_android) calls
      // postMessage into an Android object that has already been torn down as
      // the page unloads, and the Sentry SDK sees it only because it wraps
      // addEventListener handlers globally. We never call postMessage anywhere,
      // so there is nothing here to fix — matched by message as well as by
      // denyUrls below, because a frame-less variant would slip past the URL.
      /Error invoking postMessage/i,
      // The iOS twin of the above. FBIOS's in-app browser injects a WKWebView
      // performance logger (sendDataToNative / processLargestContentfulPaintEvent)
      // that reads window.webkit.messageHandlers, absent in that context, so it
      // throws. denyUrls can't catch it: its frames sit on the document root, not
      // iabjs://. Our own webkit.messageHandlers use is always guarded, so a
      // thrown one is never ours.
      /webkit\.messageHandlers/i,
    ],
    // Code that is not ours, by origin rather than by message. Browser
    // extensions inject over the page; iabjs:// is Facebook's in-app browser
    // doing the same on Android, and anyone arriving from a Facebook link is
    // running inside it.
    denyUrls: [
      /^chrome-extension:\/\//,
      /^moz-extension:\/\//,
      /^safari-extension:\/\//,
      /^iabjs:\/\//,
    ],
    tracesSampleRate: TRACES_SAMPLE_RATE,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });

  // Load Session Replay lazily so its heavy bundle stays out of the entry chunk.
  void import('@sentry/react')
    .then(({ replayIntegration, addIntegration }) => {
      addIntegration(replayIntegration({ maskAllText: true, blockAllMedia: true }));
    })
    // Replay is optional; if its chunk fails to load (e.g. a stale build after a
    // deploy serves index.html for the chunk), skip it rather than let the
    // rejection surface as an unhandled "not a valid JavaScript MIME type" error.
    .catch(() => {});
}

export function captureException(error: unknown, context?: Record<string, unknown>): void {
  if (!DSN) return;
  Sentry.captureException(error, context ? { extra: context } : undefined);
}
